import type { DeviceNetwork } from '../model.js';

export interface ListedAddress {
  readonly address: string;
  readonly prefix: number;
  readonly interface: string;
  readonly family: 4 | 6;
  readonly scope: 'global' | 'private';
  readonly primary: boolean;
}

const family = (address: string): 4 | 6 => (address.includes(':') ? 6 : 4);
function scope(address: string): 'global' | 'private' | null {
  if (family(address) === 4) {
    const [a = 0, b = 0] = address.split('.').map(Number);
    if (a === 127 || (a === 169 && b === 254) || a === 0) return null;
    return a === 10 ||
      (a === 172 && b >= 16 && b < 32) ||
      (a === 192 && b === 168) ||
      (a === 100 && b >= 64 && b < 128)
      ? 'private'
      : 'global';
  }
  const first = parseInt(address.split(':')[0] || '0', 16);
  if (address === '::1' || (first & 0xffc0) === 0xfe80) return null;
  return (first & 0xfe00) === 0xfc00 ? 'private' : 'global';
}

/**
 * Inline address first (primary IPv4, else primary IPv6), then the rest. Loopback and
 * link-local addresses never appear, even if an older client reported them.
 */
export function addressList(network: DeviceNetwork) {
  const primaries = [network.primaryIpv4, network.primaryIpv6].filter(
    (a): a is string => !!a && scope(a) != null,
  );
  const seen = new Set<string>();
  const all: ListedAddress[] = [];
  const add = (address: string, prefix: number, name: string) => {
    const s = scope(address);
    if (!s || seen.has(address)) return;
    seen.add(address);
    all.push({
      address,
      prefix,
      interface: name,
      family: family(address),
      scope: s,
      primary: primaries.includes(address),
    });
  };
  for (const address of primaries) {
    const reported = network.addresses.find((a) => a.address === address);
    add(address, reported?.prefix ?? (family(address) === 4 ? 32 : 128), reported?.interface ?? '');
  }
  for (const a of network.addresses)
    if (a.interface !== 'lo') add(a.address, a.prefix, a.interface);
  const [primary, ...others] = all;
  return { primary, others, all };
}
